import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Logo } from "../components/Logo";
import { api, mediaUrl } from "../lib/api";
import { Avatar, CreatorName, formatNum } from "../components/common";
import { Button } from "../components/ui/button";
import { InstallButton } from "../components/InstallButton";
import {
  ArrowRight, Users, Music2, Tv, Radio, Briefcase, Heart, MessageCircle,
  Repeat2, Play, Coins, Trophy, Rocket, Star, Sparkles, ChevronRight,
  ShoppingBag, Store, Palette, BadgeCheck, Lock, BarChart3
} from "lucide-react";

const WORLDS = ["SECOND LIFE", "IMVU", "FIVEM", "VRCHAT", "ROBLOX", "+ MORE"];
const PILLARS = [
  { icon: Users, name: "SOCIAL", tag: "Build audiences", color: "var(--eblue)", desc: "VFeed, VStories & VReels. Grow a following across every virtual world." },
  { icon: Music2, name: "MUSIC", tag: "Release & earn streams", color: "var(--gold)", desc: "Drop tracks on VStar Music. Earn VirtuCoins on every qualified stream." },
  { icon: Tv, name: "TV", tag: "Build channels & shows", color: "#a78bfa", desc: "Launch a channel on VStar TV. Series, movies, talk shows & more." },
  { icon: Radio, name: "LIVE", tag: "Broadcast your life", color: "#f87171", desc: "Go live from your world. Screen-share gameplay. Earn live tips." },
  { icon: Briefcase, name: "INDUSTRY", tag: "Build a career", color: "#34d399", desc: "Climb VCharts, earn a VStar Score, get discovered & win awards." },
];
const EARN = [
  ["LIKE", "+0.01 VC"], ["COMMENT", "+0.03 VC"], ["REPOST", "+0.04 VC"],
  ["QUALIFIED MUSIC STREAM", "+0.05 VC"], ["QUALIFIED VIDEO STREAM", "+0.05 VC"],
];

export default function Landing() {
  const nav = useNavigate();
  const [rising, setRising] = useState([]);

  useEffect(() => { api.get("/rising-stars").then(({ data }) => setRising(data.slice(0, 6))).catch(() => {}); }, []);

  return (
    <div className="min-h-screen bg-[#030303] text-white overflow-x-hidden">
      {/* Nav */}
      <header className="fixed top-0 inset-x-0 z-50 glass border-b border-white/8">
        <div className="max-w-7xl mx-auto px-5 h-16 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-2">
            <Link to="/login"><Button variant="ghost" className="rounded-full text-sm" data-testid="landing-login-btn">Log in</Button></Link>
            <Link to="/register"><Button className="btn-gold rounded-full px-5" data-testid="landing-signup-btn">Create Your VStar</Button></Link>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden starfield">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_0%,rgba(0,110,150,0.28),transparent_55%),radial-gradient(ellipse_at_80%_90%,rgba(212,175,55,0.14),transparent_45%)]" />
        <img src={mediaUrl("https://images.unsplash.com/photo-1672872476232-da16b45c9001?w=1600&q=80")} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[640px] h-[640px] orbit-line" />
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[920px] h-[920px] orbit-line" style={{ animationDuration: "120s" }} />
        <div className="relative z-10 text-center px-5 max-w-4xl animate-rise">
          <img src="/virtustars-logo.png" alt="VirtuStars" className="w-28 md:w-40 mx-auto mb-7" style={{ filter: "drop-shadow(0 0 40px rgba(0,229,255,0.4))" }} />
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass text-xs tracking-widest text-silver mb-6"><Sparkles size={13} className="text-gold" /> AN IMJOVE TECH PLATFORM</div>
          <h1 className="font-display font-extrabold text-5xl md:text-7xl leading-[0.95] tracking-tight">
            YOUR AVATAR<br /><span className="text-gold-grad">CAN BECOME A STAR.</span>
          </h1>
          <p className="mt-6 text-base md:text-lg text-silver max-w-xl mx-auto">Music. TV. Social. Live. Business. One industry built for virtual creators.</p>
          <div className="mt-9 flex flex-col sm:flex-row gap-3 justify-center">
            <Button onClick={() => nav("/register")} className="btn-gold rounded-full h-12 px-8 text-base" data-testid="hero-enter-btn">ENTER VIRTUSTARS <ArrowRight size={18} className="ml-1" /></Button>
            <Button onClick={() => document.getElementById("industry")?.scrollIntoView({ behavior: "smooth" })} variant="outline" className="rounded-full h-12 px-8 text-base border-white/20 bg-white/5 hover:bg-white/10" data-testid="hero-explore-btn">EXPLORE THE INDUSTRY</Button>
            <InstallButton variant="hero" />
          </div>
          <div className="mt-10 flex items-center justify-center gap-6 text-xs tracking-widest text-muted-foreground font-display">
            <span>YOUR AVATAR.</span><span className="text-eblue">YOUR CAREER.</span><span>YOUR INDUSTRY.</span>
          </div>
        </div>
      </section>

      {/* WORLDS */}
      <section className="py-20 border-y border-white/8 bg-[#06070b]">
        <div className="max-w-5xl mx-auto px-5 text-center">
          <h2 className="font-display font-bold text-3xl md:text-4xl">YOUR WORLD WAS <span className="text-blue-grad">ONLY THE BEGINNING.</span></h2>
          <div className="mt-10 overflow-hidden">
            <div className="marquee">
              {[...WORLDS, ...WORLDS].map((w, i) => (<span key={i} className="font-display font-bold text-2xl md:text-3xl text-white/25 hover:text-white transition-colors">{w}</span>))}
            </div>
          </div>
          <p className="mt-10 font-display text-xl text-silver">ONE IDENTITY. <span className="text-gold-grad">EVERY WORLD.</span></p>
          <p className="mt-3 text-muted-foreground max-w-xl mx-auto text-sm">Your VirtuStars career follows your avatar between virtual worlds — without ever leaving the community you already love.</p>
        </div>
      </section>

      {/* PILLARS */}
      <section id="industry" className="py-24 max-w-6xl mx-auto px-5">
        <div className="text-center mb-14"><div className="text-xs tracking-widest text-gold mb-2">WELCOME TO</div><h2 className="font-display font-bold text-3xl md:text-5xl">THE VIRTUAL ENTERTAINMENT INDUSTRY</h2></div>
        <div className="grid md:grid-cols-5 gap-4">
          {PILLARS.map((p) => (
            <div key={p.name} className="group surface rounded-2xl p-6 card-hover relative overflow-hidden">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ background: `${p.color}22`, color: p.color }}><p.icon size={22} /></div>
              <div className="font-display font-bold text-lg">{p.name}</div>
              <div className="text-xs mb-3" style={{ color: p.color }}>{p.tag}</div>
              <p className="text-sm text-muted-foreground leading-relaxed">{p.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CHARTS */}
      <section className="py-20 bg-[#06070b] border-y border-white/8">
        <div className="max-w-5xl mx-auto px-5">
          <div className="text-center mb-12"><h2 className="font-display font-bold text-3xl md:text-4xl">THE <span className="text-gold-grad">VSTAR CHARTS</span></h2></div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[["#1 Artist", "Nova Celeste"], ["#1 Influencer", "Luna Mirage"], ["#1 DJ", "Zane Orbit"], ["#1 Model", "Isla Rook"], ["#1 TV Show", "Avatar After Dark"], ["#1 Streamer", "Rex Dynamo"]].map(([rank, name], i) => (
              <div key={i} className="surface rounded-xl p-5 flex items-center gap-4 card-hover">
                <span className="font-mono text-gold text-xl">{rank.split(" ")[0]}</span>
                <div><div className="text-xs text-muted-foreground">{rank.split(" ").slice(1).join(" ")}</div><div className="font-display font-semibold">{name}</div></div>
                <Trophy className="ml-auto text-gold/40" size={20} />
              </div>
            ))}
          </div>
          <div className="text-center mt-8"><Link to="/register" className="text-eblue text-sm hover:underline inline-flex items-center gap-1">VIEW LIVE CHARTS <ChevronRight size={15} /></Link></div>
        </div>
      </section>

      {/* EARN */}
      <section className="py-24 max-w-5xl mx-auto px-5">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="font-display font-bold text-3xl md:text-4xl">GET PAID FOR <span className="text-gold-grad">YOUR INFLUENCE</span></h2>
            <p className="mt-4 text-muted-foreground">Earn VirtuCoins on qualified, monetized engagement under VirtuStars creator-program rules. Every meaningful interaction builds your career — and your wallet.</p>
            <div className="mt-6 inline-flex items-center gap-3 surface-2 rounded-2xl px-5 py-4">
              <Coins className="text-gold" size={28} />
              <div><div className="text-xs text-muted-foreground">Your VWallet</div><div className="font-mono text-2xl text-gold">◈ 1,204.00 VC</div></div>
            </div>
          </div>
          <div className="space-y-2.5">
            {EARN.map(([label, amt]) => (
              <div key={label} className="flex items-center justify-between surface rounded-xl px-5 py-3.5">
                <span className="text-sm text-silver tracking-wide">{label}</span>
                <span className="font-mono text-gold">{amt}</span>
              </div>
            ))}
            <p className="text-[11px] text-muted-foreground/70 px-1 pt-1">1 VC = $1.00 USD redemption value under eligible creator-program terms.</p>
          </div>
        </div>
      </section>

      {/* COMMERCE — Store & Marketplace */}
      <section id="commerce" className="py-24 max-w-6xl mx-auto px-5">
        <div className="text-center mb-14">
          <div className="text-xs tracking-widest text-gold mb-2">SPEND & SELL WITH VIRTUCOINS</div>
          <h2 className="font-display font-bold text-3xl md:text-5xl">A BUILT-IN <span className="text-gold-grad">CREATOR ECONOMY</span></h2>
          <p className="mt-4 text-muted-foreground max-w-2xl mx-auto text-sm">Turn your VirtuCoins into profile power — or turn your talent into income. The VirtuStars Shop brings a full marketplace to the industry.</p>
        </div>
        <div className="grid md:grid-cols-2 gap-5">
          <div className="surface rounded-3xl p-8 card-hover relative overflow-hidden" data-testid="landing-store-card">
            <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full" style={{ background: "radial-gradient(circle, rgba(212,175,55,0.18), transparent 70%)" }} />
            <div className="relative">
              <div className="w-12 h-12 rounded-xl bg-gold/15 text-gold flex items-center justify-center mb-4"><ShoppingBag size={22} /></div>
              <div className="text-xs tracking-widest text-gold">OFFICIAL STORE</div>
              <h3 className="font-display font-bold text-2xl mt-1">Customize your star</h3>
              <p className="text-sm text-muted-foreground mt-3 leading-relaxed">Spend VirtuCoins on exclusive profile upgrades that make your presence unmistakable across every virtual world.</p>
              <ul className="mt-5 space-y-2.5">
                {[["Animated profile effects", Sparkles], ["Premium backgrounds & frames", Palette], ["Collectible badges & flair", BadgeCheck]].map(([t, I]) => (
                  <li key={t} className="flex items-center gap-2.5 text-sm text-silver"><I size={16} className="text-gold shrink-0" />{t}</li>
                ))}
              </ul>
            </div>
          </div>
          <div className="surface rounded-3xl p-8 card-hover relative overflow-hidden" data-testid="landing-marketplace-card">
            <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full" style={{ background: "radial-gradient(circle, rgba(0,229,255,0.18), transparent 70%)" }} />
            <div className="relative">
              <div className="w-12 h-12 rounded-xl bg-eblue/15 text-eblue flex items-center justify-center mb-4"><Store size={22} /></div>
              <div className="text-xs tracking-widest text-eblue">CREATOR MARKETPLACE</div>
              <h3 className="font-display font-bold text-2xl mt-1">Sell your creations</h3>
              <p className="text-sm text-muted-foreground mt-3 leading-relaxed">Open a storefront and sell your digital goods to the community. Buyers unlock instantly and you earn VirtuCoins on every single sale.</p>
              <ul className="mt-5 space-y-2.5">
                {[["Gated digital downloads", Lock], ["Your own public storefront", Store], ["Seller analytics & payouts", BarChart3]].map(([t, I]) => (
                  <li key={t} className="flex items-center gap-2.5 text-sm text-silver"><I size={16} className="text-eblue shrink-0" />{t}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
        <div className="text-center mt-8"><Button onClick={() => nav("/register")} variant="outline" className="rounded-full border-white/20 bg-white/5" data-testid="commerce-cta-btn">EXPLORE THE SHOP <ChevronRight size={15} className="ml-1" /></Button></div>
      </section>

      {/* RISING STARS */}
      <section className="py-20 bg-[#06070b] border-y border-white/8">
        <div className="max-w-6xl mx-auto px-5">
          <div className="flex items-center gap-2 justify-center mb-10"><Rocket className="text-gold" /><h2 className="font-display font-bold text-3xl md:text-4xl">RISING STARS</h2></div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {rising.map((u) => (
              <div key={u.id} className="surface rounded-2xl p-5 card-hover">
                <div className="flex items-center gap-3">
                  <Avatar src={u.avatar_url} name={u.display_name} size={52} />
                  <div className="min-w-0"><div className="font-semibold flex items-center gap-1"><CreatorName user={u} /></div><div className="text-xs text-muted-foreground">@{u.username} · {u.worlds?.[0]}</div></div>
                </div>
                <div className="flex items-center justify-between mt-4 text-sm">
                  <span className="text-muted-foreground">{u.roles?.primary}</span>
                  <span className="text-gold font-mono">▲ {u.growth}%</span>
                </div>
                <div className="flex items-center justify-between mt-2 text-xs text-muted-foreground"><span>{formatNum(u.follower_count)} followers</span><span>VStar {Number(u.vstar_score).toFixed(0)}</span></div>
              </div>
            ))}
          </div>
          <div className="text-center mt-8"><Button onClick={() => nav("/register")} variant="outline" className="rounded-full border-white/20 bg-white/5" data-testid="discover-rising-btn">DISCOVER RISING STARS</Button></div>
        </div>
      </section>

      {/* AWARDS */}
      <section className="py-28 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_50%,rgba(212,175,55,0.12),transparent_60%)]" />
        <div className="relative max-w-4xl mx-auto px-5 text-center">
          <Trophy className="w-14 h-14 mx-auto text-gold mb-6" />
          <h2 className="font-display font-extrabold text-4xl md:text-6xl">FROM AVATAR<br /><span className="text-gold-grad">TO ICON.</span></h2>
          <p className="mt-5 text-muted-foreground max-w-xl mx-auto">The VirtuStars Awards celebrate the top creators across the industry with annual recognition and permanent career achievements.</p>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            {["Artist of the Year", "Influencer of the Year", "TV Show of the Year", "Director of the Year", "Streamer of the Year"].map((a) => (
              <span key={a} className="glass glass-gold rounded-full px-4 py-2 text-sm text-silver">{a}</span>
            ))}
          </div>
          <Button onClick={() => nav("/register")} className="btn-gold rounded-full h-12 px-8 mt-10" data-testid="awards-explore-btn">EXPLORE THE INDUSTRY</Button>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-28 bg-[#06070b] border-t border-white/8 text-center px-5">
        <h2 className="font-display font-extrabold text-4xl md:text-6xl">YOUR VIRTUAL CAREER<br /><span className="text-blue-grad">STARTS HERE.</span></h2>
        <div className="mt-6 space-y-1 text-silver"><p>Create your identity. Build your audience.</p><p>Earn VirtuCoins. Become a VirtuStar.</p></div>
        <Button onClick={() => nav("/register")} className="btn-gold rounded-full h-14 px-10 text-lg mt-9" data-testid="final-cta-btn"><Star size={20} className="mr-1" /> CREATE YOUR VSTAR</Button>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-white/8 py-12 px-5">
        <div className="max-w-6xl mx-auto grid md:grid-cols-4 gap-8">
          <div><Logo /><p className="text-sm text-muted-foreground mt-3">An ImJove Tech Platform.<br />Your Avatar. Your Career. Your Industry.</p></div>
          {[["Platform", ["About", "Community Guidelines", "Safety", "Support"]], ["Legal", ["Terms", "Privacy", "Data Policy", "Creator Monetization Terms"]], ["Discover", ["Charts", "Industry", "Awards", "Rising Stars"]]].map(([h, items]) => (
            <div key={h}><div className="font-display font-semibold mb-3">{h}</div><ul className="space-y-2 text-sm text-muted-foreground">{items.map((i) => {
              const legal = { "Terms": "/legal/terms", "Privacy": "/legal/privacy", "Data Policy": "/legal/data", "Creator Monetization Terms": "/legal/terms", "Community Guidelines": "/legal/terms" }[i];
              return legal ? <li key={i}><Link to={legal} className="hover:text-white transition-colors">{i}</Link></li> : <li key={i} className="hover:text-white cursor-pointer transition-colors">{i}</li>;
            })}</ul></div>
          ))}
        </div>
        <div className="max-w-6xl mx-auto mt-10 pt-6 border-t border-white/8 text-xs text-muted-foreground/60">© 2026 VirtuStars · ImJove Tech. All rights reserved.</div>
      </footer>
    </div>
  );
}
